import { useCourseDetails } from "../../../contexts/CourseDetailsContext";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import { useAppConfig } from "../../../AppConfigProvider";
import { StyleSheet, Animated, View, Pressable } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import useResponsiveScreen from "../../../hooks/useResponsiveScreen";
import CustomHtml from "../../../components/common/general/CustomHtml/CustomHtml";
import { ScrollView } from "react-native-gesture-handler";
import ModuleAccordion from "../../../components/customCoursework/CourseCatalogue/ModuleAccordion/ModuleAccordion";

import { Link } from "@react-navigation/native";
import { useAuth } from "../../../hooks/useAuth";

function SlideInCourseDetails({ visible, slideAnim, onClose, notifications = [] }) {
  const { onEnroll, courseData, loading } = useCourseDetails();
  const appConfig = useAppConfig();
  const primaryColor = appConfig.primaryColor;
  const { isMedium, isXLarge } = useResponsiveScreen();
  const { userInfo } = useAuth();

  const handleEnrollPress = (id, cost) => {
    onEnroll(id, cost);
  };

  if (!visible) return null;

  return (
    <Animated.View
      style={[
        styles.container,
        { transform: [{ translateX: slideAnim }], width: isXLarge ? 500 : isMedium ? 370 : "100%" },
      ]}>
      <View style={styles.innerContainer}>
        <View style={styles.topRow}>
          <CustomText
            size="m"
            weight="bold"
            useTranslationText={false}
            style={styles.headerText}
            text={courseData?.name}
          />
          <Pressable role="button" aria-label="Close course detail" style={styles.closeButton} onPress={onClose}>
            <Ionicons name="close" size={24} color="black" />
          </Pressable>
        </View>

        <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={true} tabIndex={0}>
          <View style={styles.contentContainer}>
            <View style={styles.notificationsList}>
              <CustomText text="Course Description" size="xl" weight="ultraBold" style={{ color: "#101828" }} />
              <CustomSpacing type="vertical" size="l" />
              <CustomHtml
                html={courseData?.description || "<p>No description available</p>"}
                style={styles.htmlTextStyle}
              />
              <CustomSpacing type="vertical" size="xl" />
              <CustomText text="Course Prerequisites" size="xl" weight="ultraBold" style={{ color: "#101828" }} />
              <CustomSpacing type="vertical" size="l" />
              <CustomText
                text="The following courses must be completed before you can enroll in this course."
                size="m"
                style={{ color: "#667085" }}
              />
              <CustomSpacing type="vertical" size="l" />
              <View style={styles.preRequisitesList}>
                {courseData?.preRequisites?.map((prerequisite) => {
                  const isUserEnrolled = prerequisite?.enrolledStudents?.some((user) => user.id === userInfo.userId);

                  return (
                    <View key={prerequisite.id} style={styles.preRequisiteItem}>
                      <View style={styles.preRequisiteItemTitleRow}>
                        <CustomText text={prerequisite.name} size="m" weight="bold" useTranslationText={false} />
                        {!!prerequisite.cost && prerequisite?.cost > 0 && (
                          <CustomText
                            text={`$${prerequisite.cost}`}
                            size="m"
                            weight="bold"
                            useTranslationText={false}
                          />
                        )}
                      </View>
                      <CustomText text={prerequisite.description} size="s" weight="light" useTranslationText={false} />
                      {!isUserEnrolled && (
                        <CustomButton
                          text={"Enroll"}
                          size="s"
                          onPress={() => handleEnrollPress(prerequisite?.id, !!prerequisite?.cost)}
                          style={[{ backgroundColor: primaryColor }, {}]}
                          textStyle={styles.enrollButtonText}
                          disabled={loading}
                        />
                      )}
                      {isUserEnrolled && (
                        <Link to={`/student/school/course?course_id=${prerequisite.id}`}>
                          <CustomButton
                            text={"View Course"}
                            size="s"
                            style={[{ backgroundColor: primaryColor }, {}]}
                            textStyle={styles.enrollButtonText}
                            disabled={loading}
                            onPress={() => {}}
                          />
                        </Link>
                      )}
                    </View>
                  );
                })}
              </View>
              <CustomSpacing type="vertical" size="xl" />
              <CustomText text="What You'll Learn" size="xl" weight="ultraBold" style={{ color: "#101828" }} />
              <CustomSpacing type="vertical" size="l" />
              <CustomHtml
                html={courseData?.objectives?.[0] || "<p>No content available</p>"}
                style={styles.htmlTextStyle}
              />
              <CustomSpacing type="vertical" size="l" />
              <CustomSpacing type="vertical" size="xl" />

              <CustomText text="Weekly Breakdown" size="xl" weight="ultraBold" style={{ color: "#101828" }} />
              <CustomSpacing type="vertical" size="l" />

              {courseData?.modules && courseData.modules.length > 0 ? (
                courseData.modules.map((module, index) => (
                  <ModuleAccordion key={`module-${index}`} module={module} index={index} />
                ))
              ) : (
                <CustomText
                  text="No modules available"
                  size="m"
                  useTranslationText={false}
                  style={{ color: "#667085" }}
                />
              )}

              <CustomSpacing type="vertical" size="l" />
              <View style={styles.targetAudienceBox}>
                <CustomText text="Ideal For" size="xl" weight="ultraBold" style={{ color: "#101828" }} />
                <CustomHtml
                  html={courseData?.targetAudience || "<p>No information available</p>"}
                  style={styles.htmlTextStyle}
                />
              </View>
            </View>
          </View>
        </ScrollView>
        <View style={styles.enrollButtonContainer}>
          <CustomButton
            text={
              courseData?.preRequisitesCompleted
                ? courseData?.cost
                  ? `Enroll for $${courseData?.cost}`
                  : "Enroll"
                : "Pre-Req Not Met"
            }
            onPress={() => handleEnrollPress(courseData?.id, !!courseData?.cost)}
            style={[
              { backgroundColor: courseData?.preRequisitesCompleted ? primaryColor : "#667085" },
              styles.enrollButtonStyle,
            ]}
            textStyle={styles.enrollButtonText}
            disabled={loading}
          />
        </View>
      </View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    height: 20,
  },
  container: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    zIndex: 100,
  },
  innerContainer: {
    flex: 1,
    backgroundColor: "white",
    borderLeftWidth: 1,
    borderLeftColor: "#e0e0e0",
  },
  topRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
  },
  headerText: {
    fontSize: 18,
  },
  closeButton: {
    padding: 5,
  },
  contentContainer: {
    flex: 1,
    padding: 16,
  },
  notificationsList: {
    flex: 1,
  },
  enrollButtonText: {
    color: "white",
    fontWeight: "700",
  },
  enrollButtonStyle: {
    width: "100%",
    padding: 8,
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderRadius: 8,
  },
  enrollButtonContainer: {
    padding: 16,
  },
  targetAudienceBox: {
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    padding: 20,
    gap: 8,
    backgroundColor: "#F2F4F7",
    borderRadius: 20,
  },
  htmlTextStyle: {
    color: "#344054",
    fontSize: 18,
    lineHeight: 28,
    fontFamily: "Inter",
    fontWeight: "400",
  },
  preRequisitesList: {
    width: "100%",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    gap: 8,
  },
  preRequisiteItem: {
    width: "100%",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    gap: 8,
    borderWidth: 1,
    borderColor: "#e0e0e0",
    borderRadius: 8,
    padding: 16,
  },
  preRequisiteItemTitleRow: {
    width: "100%",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
  },
});

export default SlideInCourseDetails;
